import { useEffect } from 'react'
import styled from 'styled-components'

import useExclusivelyScrollableNode from 'hooks/useExclusivelyScrollableNode'
import { useGetCountriesQuery } from 'hooks/useQueries'
import { BasePanel } from 'components/display/Panel'
import { searchResultsPanelZIndex, minSearchQueryLength } from '../../constants'
import { ResultListProps } from '../types'
import ResultsList from './ResultsList'
import SearchableCountry from './SearchableCountry'
import { SpinnerWrapper } from './styles'

const PanelOuter = styled(BasePanel)<{ open: boolean }>``

const PanelInner = styled.div<{ mobile: boolean }>`
  z-index: ${searchResultsPanelZIndex};
  width: 100%;
  margin-top: -4px;
  left: 0;
  background: ${({ theme }) => theme['-ds-neutral-100'].value};
  padding: 8px;
  overflow: auto;

  ${({ mobile, theme }) =>
    !mobile &&
    `
    position: absolute;
    top: 100%;
    border: 1px solid ${theme['-ds-neutral-400'].value};
    border-top: none;
    border-radius: 0 0 6px 6px;
    max-height: calc(100vh - 100px);
  `}

  ${({ mobile }) =>
    mobile &&
    `
    top: 60px; /* leave enough room for search input */
    position: fixed;
    bottom: 0px;
    right: 0px;
    max-height: calc(100vh - 57px);
  `};
`

const SearchableShortcutsList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
`

type ResultsProps = {
  mobile: boolean
  query: string
  onShortcutSelect: (_: { country: string; state: string }) => any
  onClose: () => any
  results: ResultListProps
  loading: boolean
  sessionToken: string
}

const Results = ({
  mobile,
  query,
  onShortcutSelect,
  onClose,
  results,
  loading,
  sessionToken,
}: ResultsProps) => {
  const { data: countriesData } = useGetCountriesQuery()

  const exclusivelyScrollable = useExclusivelyScrollableNode<HTMLDivElement>({
    skip: !mobile,
  })

  const searchableCountries = countriesData?.defaults?.countries?.filter(
    ({ discoverable }) => discoverable === true,
  )

  const handleScroll = () => {
    if (window.scrollY > 300) onClose()
  }

  useEffect(() => {
    if (!mobile) {
      window.addEventListener('scroll', handleScroll)
    }

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <PanelOuter open>
      <PanelInner mobile={mobile} ref={exclusivelyScrollable}>
        {query && query.trim().length < minSearchQueryLength && (
          <SpinnerWrapper>
            <em>{`Keep typing (min ${minSearchQueryLength} characters required)`}</em>
          </SpinnerWrapper>
        )}

        {query && query.trim().length >= minSearchQueryLength && (
          <div>
            <ResultsList
              onSelect={onClose}
              results={results}
              loading={loading}
              searchableCountries={searchableCountries}
              sessionToken={sessionToken}
            />
          </div>
        )}

        {!query && (
          <div>
            <SearchableShortcutsList>
              {searchableCountries?.map(({ id, code, name }) => (
                <SearchableCountry
                  key={id}
                  countryCode={code}
                  countryName={name}
                  onShortcutSelect={onShortcutSelect}
                />
              ))}
            </SearchableShortcutsList>
          </div>
        )}
      </PanelInner>
    </PanelOuter>
  )
}

export default Results
